import Image from "next/image";
import Link from "next/link";
import { ChatBubbleOvalLeftIcon } from "@heroicons/react/24/outline";
import { ReactNode, ComponentType } from "react";
import { renderWithBrackets } from "../../lib/renderWithBrackets";

interface Feature {
	icon: ComponentType<{ className?: string }>;
	text: string;
	iconColor?: string; // Color class for icon background (e.g., 'bg-green', 'bg-pink', 'bg-yellow')
}

interface CTAButton {
	text: string;
	href: string;
	icon?: ComponentType<{ className?: string }>;
	variant?: "primary" | "secondary";
}

interface FullBackgroundHeroProps {
	backgroundImage: string;
	headline: ReactNode;
	subheadline: ReactNode;
	features?: Feature[];
	ctaText?: string;
	ctaIcon?: ComponentType<{ className?: string }> | null;
	ctaHref?: string;
	bracketedText?: string; // Text portion to wrap with brackets
	bracketAnimateColors?: boolean; // Whether brackets should animate colors
	ctaButtons?: CTAButton[]; // Array of CTA buttons for multiple CTAs
	showColorfulLine?: boolean; // Whether to show colorful line at bottom
}

export default function FullBackgroundHero({
	backgroundImage,
	headline,
	subheadline,
	features,
	ctaText,
	ctaIcon: CtaIcon = ChatBubbleOvalLeftIcon,
	ctaHref = "/contact",
	bracketedText,
	bracketAnimateColors = false,
	ctaButtons,
	showColorfulLine = false,
}: FullBackgroundHeroProps) {
	const hasFeatures = features && features.length > 0;
	
	const renderHeadlineWithBrackets = () => {
		return renderWithBrackets({
			text: headline,
			bracketedText,
			bracketAnimateColors,
			fontSize: {
				min: "28px",
				preferred: "3.33vw",
				max: "48px",
			},
			lineHeightMultiplier: 1.25, // leading-tight
		});
	};

	return (
		<div className="relative w-full min-h-[90vh] bg-slate-900 flex items-center justify-center overflow-hidden px-2 xl:px-0">
			<Image
				src={backgroundImage}
				alt="Background"
				layout="fill"
				objectFit="cover"
				className="z-0"
			/>
			<div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-black z-0 opacity-80 mix-blend-multiply"></div>

			<div className="relative z-10 container mx-auto px-4 pt-32 pb-20 max-w-6xl">
				<div className={`mx-auto ${hasFeatures ? "mb-16" : "mb-8"}`}>
					<h1 className="text-[clamp(28px,3.33vw,48px)] leading-tight font-display font-bold text-white mb-6">
						{renderHeadlineWithBrackets()}
					</h1>
					<p className="text-[clamp(18px,1.67vw,24px)] text-white max-w-6xl">{subheadline}</p>
				</div>

				{features && features.length > 0 && (
					<div className="flex flex-wrap gap-6 mb-16 text-white max-w-6xl">
						{features.map((feature, index) => {
							const Icon = feature.icon;
							return (
								<div
									key={index}
									className="flex items-center gap-3"
								>
									<div
										className={`w-8 h-8 rounded-md flex items-center justify-center ${
											feature.iconColor || "bg-white"
										}`}
									>
										<Icon className="w-5 h-5 text-white" />
									</div>
									<p className="text-base font-display font-medium">{feature.text}</p>
								</div>
							);
						})}
					</div>
				)}

				<div className="text-left flex flex-wrap gap-4">
					{ctaButtons && ctaButtons.length > 0 ? (
						ctaButtons.map((cta, index) => {
							const ButtonIcon = cta.icon || ChatBubbleOvalLeftIcon;
							const isPrimary = cta.variant !== "secondary";
							return (
								<Link key={index} href={cta.href}>
									<a
										className={`font-display py-4 px-8 rounded-md transition-colors shadow-lg text-lg flex items-center ${
											isPrimary
												? "bg-blue text-white hover:bg-blue/80"
												: "bg-white/10 text-white border border-white/30 hover:bg-white/20"
										}`}
									>
										<ButtonIcon className="w-6 h-6 mr-2" />
										{cta.text}
									</a>
								</Link>
							);
						})
					) : ctaText ? (
						<Link href={ctaHref}>
							<a className="bg-blue text-white font-display py-4 px-12 rounded-md hover:bg-blue/80 transition-colors shadow-lg text-lg flex items-center">
								{CtaIcon && <CtaIcon className="w-6 h-6 mr-2" />} {ctaText}
							</a>
						</Link>
					) : null}
				</div>
			</div>

			{showColorfulLine && (
				<div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
					<div className="flex-1 bg-blue"></div>
					<div className="flex-1 bg-green"></div>
					<div className="flex-1 bg-red"></div>
					<div className="flex-1 bg-yellow"></div>
				</div>
			)}
		</div>
	);
}
